// src/pages/RoleBasedDashboard.jsx
import React, { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

// FIX IMPORTS: Import the functional versions of the dashboards
import AdminOwnerContent from './admin/AdminOwnerContent';
import ExamAdminDashboard from './admin/ExamAdminDashboard';
import StudentContent from './student/StudentContent';

// --- NEW IMPORTS: For the Examiner and Educator roles ---
import ExaminerDashboard from './examiner/ExaminerDashboard'; // This is the fully integrated grading panel
import StudentDashboard from './student/Dashboard'; // Assuming StudentContent uses/renders this StudentDashboard

// ===================================================================
// Dashboard Shell (Unchanged - Keeps your layout structure)
// ===================================================================
const DashboardShell = ({ user, userProfile, userRole, logout, title, children }) => {
    const getRoleStyle = (role) => {
        const styles = {
            owner: 'bg-red-600 text-white',
            admin: 'bg-gray-600 text-white',
            exam_admin: 'bg-purple-600 text-white',
            student: 'bg-blue-600 text-white',
            editor: 'bg-green-600 text-white', // Placeholder for new admin roles
            finance_admin: 'bg-orange-600 text-white', // Placeholder for new admin roles
            educator: 'bg-green-600 text-white',
            examiner: 'bg-indigo-600 text-white',
            default: 'bg-gray-400 text-white'
        };
        // Use a broader check for any role containing 'admin' or 'owner' if needed, but stick to explicit roles here
        return styles[role] || (role.includes('admin') ? styles.admin : styles.default);
    };

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col">
            <nav className="bg-white shadow-sm p-4 flex justify-between items-center sticky top-0 z-20 border-b">
                <h1 className="text-xl font-bold text-gray-800">Math Craft Portal</h1>
                <div className="flex items-center space-x-4">
                    <span className="text-gray-600 text-sm hidden md:block">
                        {userProfile?.email || user?.email}
                        <span className={`ml-2 px-3 py-1 rounded-full text-xs font-semibold ${getRoleStyle(userRole)}`}>
                            {userRole || 'guest'}
                        </span>
                    </span>
                    <button onClick={logout} className="bg-gray-700 hover:bg-gray-800 text-white py-2 px-4 rounded-md text-sm font-semibold">
                        Logout
                    </button>
                </div>
            </nav>

            <main className="flex-grow p-4 md:p-6">
                <div className="max-w-7xl mx-auto">
                    <header className="mb-6">
                        <h2 className="text-3xl font-bold text-gray-900">{title}</h2>
                    </header>
                    {children}
                </div>
            </main>
        </div>
    );
};

// ===================================================================
// Base Dashboard Components (Used for roles without dedicated pages)
// ===================================================================
// NOTE: We keep this Educator Dashboard simple, as its main features are the Chat and Staff Tickets.
const EducatorBaseDashboard = () => (
    <div className="p-8 text-center bg-green-100 border border-green-300 rounded-lg">
        <h2 className="text-2xl font-bold text-green-800">Educator Base Dashboard</h2>
        <p className="mt-2 text-green-700">Access your student chat and support requests via the sidebar/navigation.</p>
    </div>
);

// We remove the old ExaminerDashboard placeholder.


// ===================================================================
// Main Role-Based Dashboard
// ===================================================================
export default function RoleBasedDashboard() {
    const { user, userProfile, loading, settingsLoading, logout } = useAuth();

    // Debug logs
    useEffect(() => {
        console.log("RoleBasedDashboard Auth Data:", { user, userProfile, loading, settingsLoading });
    }, [user, userProfile, loading, settingsLoading]);

    // 1️⃣ Loading state
    if (loading || settingsLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-gray-500">
                Initializing Portal...
            </div>
        );
    }

    // 2️⃣ Auth / profile check
    if (!user || !userProfile) {
        console.warn("RoleBasedDashboard accessed without authenticated userProfile.");
        return (
            <div className="min-h-screen flex items-center justify-center text-red-500">
                Authentication Error. Please log in.
                <button onClick={logout} className="ml-4 bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-800">
                    Logout
                </button>
            </div>
        );
    }

    const userRole = userProfile.role || 'guest';
    const dashboardTitle = `${userRole.charAt(0).toUpperCase() + userRole.slice(1)} Dashboard`;

    // 3️⃣ Render role-based content
    const renderContent = () => {
        // Use a switch statement to render the CORRECT component
        switch (userRole) {
            case 'owner':
            case 'admin':
                // Admin/Owner is the top-level manager
                return <AdminOwnerContent />;
            case 'exam_admin':
                // Exam Admin needs the specialized exam management tools
                return <ExamAdminDashboard />;
            case 'educator':
                // Educator gets a simple shell; core features are external links (Chat, Tickets)
                return <EducatorBaseDashboard />;
            case 'examiner':
                // Examiner gets the fully integrated Grading Interface
                return <ExaminerDashboard />;
            case 'student':
            case 'guest':
                // StudentContent should render the StudentDashboard which has all the links
                return <StudentContent />;
            default:
                // Any other custom role will get the default message
                return (
                    <div className="p-8 text-center bg-yellow-100 border border-yellow-300 rounded-lg">
                        <h2 className="text-xl font-semibold text-yellow-800">Role '{userRole}' Not Recognized</h2>
                        <p className="mt-2 text-yellow-700">No dashboard is configured for your assigned role.</p>
                        <button onClick={logout} className="mt-4 bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">
                            Logout
                        </button>
                    </div>
                );
        }
    };

    return (
        <DashboardShell
            user={user}
            userProfile={userProfile}
            userRole={userRole}
            logout={logout}
            title={dashboardTitle}
        >
            {renderContent()}
        </DashboardShell>
    );
}
